import { useState } from 'react';
import { Loader2, Star } from 'lucide-react';
import { Modal } from '@shared/components/ui/Modal';

type Props = {
  open: boolean;
  onClose: () => void;
  onSubmit: (rating: number) => Promise<void>;
};

export function AiInterviewExperienceModal({ open, onClose, onSubmit }: Props) {
  const [selected, setSelected] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  async function submit() {
    if (selected == null) return;
    setBusy(true);
    setErr('');
    try {
      await onSubmit(selected);
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not save. Try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={() => !busy && onClose()}
      title="How was your experience?"
      subtitle="Optional — helps us improve this interview for everyone."
      icon={<Star className="h-4 w-4" aria-hidden />}
      size="sm"
      variant="default"
    >
      <p className="mb-4 text-sm leading-relaxed text-slate-700 dark:text-slate-300">
        Rate from <strong className="text-slate-900 dark:text-slate-100">1</strong> (poor) to{' '}
        <strong className="text-slate-900 dark:text-slate-100">10</strong> (excellent).
      </p>
      <div className="mb-4 grid grid-cols-5 gap-2 sm:grid-cols-10">
        {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
          <button
            key={n}
            type="button"
            disabled={busy}
            onClick={() => setSelected(n)}
            className={`min-h-[36px] rounded-lg border text-sm font-semibold transition-colors disabled:opacity-50 ${
              selected === n
                ? 'border-violet-600 bg-violet-100 text-violet-900 dark:border-sky-500 dark:bg-sky-500/20 dark:text-sky-100'
                : 'border-slate-200 bg-white text-slate-800 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800/80 dark:text-slate-200 dark:hover:bg-slate-800'
            }`}
          >
            {n}
          </button>
        ))}
      </div>
      {err ? <p className="mb-3 text-sm text-amber-800 dark:text-amber-200/90">{err}</p> : null}
      <div className="flex justify-end gap-2">
        <button type="button" className="btn-secondary" disabled={busy} onClick={() => onClose()}>
          Skip
        </button>
        <button type="button" className="btn-primary" disabled={busy || selected == null} onClick={() => void submit()}>
          {busy ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              Saving…
            </span>
          ) : (
            'Submit'
          )}
        </button>
      </div>
    </Modal>
  );
}
